import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjects, Project } from '../utils/projectStorage';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

  useEffect(() => {
    const projects = getProjects();
    const foundProject = projects.find(p => p.id === Number(id));
    if (foundProject) {
      setProject(foundProject);
      // İlk görseli seç (images varsa ilk image, yoksa kapak görseli)
      if (foundProject.images && foundProject.images.length > 0) {
        setSelectedImage(foundProject.images[0]);
        setSelectedImageIndex(0);
      } else if (foundProject.image) {
        setSelectedImage(foundProject.image);
        setSelectedImageIndex(0);
      }
    }
  }, [id]);

  const handleImageClick = (img: string, index: number) => {
    setSelectedImage(img);
    setSelectedImageIndex(index);
  };

  const openLightbox = () => {
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextImage = () => {
    if (!project) return;
    const allImages = [
      ...(project.image ? [project.image] : []),
      ...(project.images || [])
    ].filter(img => img);
    
    const nextIndex = (selectedImageIndex + 1) % allImages.length;
    setSelectedImage(allImages[nextIndex]);
    setSelectedImageIndex(nextIndex);
  };

  const prevImage = () => {
    if (!project) return;
    const allImages = [
      ...(project.image ? [project.image] : []),
      ...(project.images || [])
    ].filter(img => img);
    
    const prevIndex = selectedImageIndex === 0 ? allImages.length - 1 : selectedImageIndex - 1;
    setSelectedImage(allImages[prevIndex]);
    setSelectedImageIndex(prevIndex);
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLightboxOpen, selectedImageIndex]);

  if (!project) {
    return (
      <div className="hero">
        <div className="container text-center">
          <h1 className="heading-1 mb-md">Proje Bulunamadı</h1>
          <Link to="/projeler" className="btn btn-primary">
            Projelere Dön
          </Link>
        </div>
      </div>
    );
  }

  const allImages = [
    ...(project.image ? [project.image] : []),
    ...(project.images || [])
  ].filter(img => img);

  return (
    <div style={{paddingTop: '100px', minHeight: '100vh'}}>
      {/* Breadcrumb */}
      <div className="container" style={{paddingTop: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)'}}>
        <div className="body-small text-secondary">
          <Link to="/" style={{color: 'var(--color-text-secondary)', textDecoration: 'none'}}>Ana Sayfa</Link>
          {' / '}
          <Link to="/projeler" style={{color: 'var(--color-text-secondary)', textDecoration: 'none'}}>Projeler</Link>
          {' / '}
          <span className="accent-text">{project.title}</span>
        </div>
      </div>

      {/* Project Header */}
      <section className="section-sm" style={{background: 'var(--color-bg-card)'}}>
        <div className="container">
          <div className="fade-in-up">
            <p className="body-small accent-text mb-sm">{project.category} • {project.year}</p>
            <h1 className="display-2 mb-md">{project.title}</h1>
            <p className="body-large text-secondary">{project.description}</p>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      {allImages.length > 0 && (
        <section className="section">
          <div className="container">
            {/* Main Image */}
            <div className="card mb-md scale-in" style={{overflow: 'hidden', position: 'relative'}}>
              <img 
                src={selectedImage} 
                alt={project.title}
                style={{
                  width: '100%', 
                  height: 'auto', 
                  maxHeight: '600px', 
                  objectFit: 'contain', 
                  background: 'var(--color-bg-dark)',
                  cursor: 'zoom-in'
                }}
                onClick={openLightbox}
              />
              {allImages.length > 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: '1rem',
                  right: '1rem',
                  background: 'rgba(0,0,0,0.7)',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  color: 'white',
                  fontSize: '0.875rem'
                }}>
                  {selectedImageIndex + 1} / {allImages.length}
                </div>
              )}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.7)',
                      border: 'none',
                      color: 'white',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      fontSize: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ‹
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.7)',
                      border: 'none',
                      color: 'white',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      fontSize: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem'}}>
                {allImages.map((img, index) => (
                  <div 
                    key={index} 
                    className={`card fade-in animate-delay-${(index % 4) + 1}`}
                    style={{
                      cursor: 'pointer',
                      overflow: 'hidden',
                      border: selectedImageIndex === index ? '3px solid var(--color-accent)' : '1px solid var(--color-border)',
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                    onClick={() => handleImageClick(img, index)}
                  >
                    <img 
                      src={img} 
                      alt={`${project.title} - ${index + 1}`}
                      style={{
                        width: '100%', 
                        height: '120px', 
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                    {selectedImageIndex === index && (
                      <div style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: 'var(--color-accent)',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-primary)',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {isLightboxOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: '2rem',
              right: '2rem',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            ×
          </button>
          
          <img 
            src={selectedImage}
            alt={project.title}
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              animation: 'scaleIn 0.3s ease'
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                style={{
                  position: 'absolute',
                  left: '2rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                ‹
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                style={{
                  position: 'absolute',
                  right: '2rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                ›
              </button>
              <div style={{
                position: 'absolute',
                bottom: '2rem',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.7)',
                padding: '0.75rem 1.5rem',
                borderRadius: '25px',
                color: 'white',
                fontSize: '1rem'
              }}>
                {selectedImageIndex + 1} / {allImages.length}
              </div>
            </>
          )}
        </div>
      )}

      {/* Project Info */}
      <section className="section-sm" style={{background: 'var(--color-bg-card)'}}>
        <div className="container">
          <div className="grid grid-2 gap-lg">
            <div className="card slide-in-left" style={{padding: 'var(--spacing-lg)'}}>
              <h2 className="heading-2 mb-md">Proje Detayları</h2>
              <div style={{marginBottom: 'var(--spacing-md)'}}>
                <p className="body-small text-muted mb-sm">Kategori</p>
                <p className="body-base accent-text">{project.category}</p>
              </div>
              <div style={{marginBottom: 'var(--spacing-md)'}}>
                <p className="body-small text-muted mb-sm">Yıl</p>
                <p className="body-base">{project.year}</p>
              </div>
              <div>
                <p className="body-small text-muted mb-sm">Görsel Sayısı</p>
                <p className="body-base">{allImages.length} Görsel</p>
              </div>
            </div>

            <div className="card slide-in-right" style={{padding: 'var(--spacing-lg)'}}>
              <h2 className="heading-2 mb-md">Açıklama</h2>
              <p className="body-base text-secondary" style={{lineHeight: '1.8'}}>
                {project.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container text-center">
          <h2 className="heading-2 mb-md">Benzer Bir Projeniz mi Var?</h2>
          <p className="body-large text-secondary mb-lg">
            Hayalinizdeki mimari projeyi birlikte gerçekleştirelim
          </p>
          <div className="flex-center gap-md">
            <Link to="/projeler" className="btn btn-outline">
              Tüm Projeler
            </Link>
            <a href="/#iletisim" className="btn btn-primary">
              İletişime Geçin
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
